import { useEffect, useRef } from "react";
import * as THREE from "three";

const STAGE_LABELS = [
  "Stage 01 — Consultation",
  "Stage 02 — Site assessment",
  "Stage 03 — Planning and estimation",
  "Stage 04 — Project execution",
  "Stage 05 — Quality checks",
  "Stage 06 — Completion and handover",
];

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readColor(el, name, fallback) {
  const value = getComputedStyle(el).getPropertyValue(name).trim();
  return value || fallback;
}

function makeRenderer(canvas) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  return renderer;
}

function sizeToParent(canvas, camera, renderer) {
  const parent = canvas.parentElement;
  if (!parent) return;
  const { width, height } = parent.getBoundingClientRect();
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h, false);
}

function edges(geometry, color, opacity = 0.9, role = "ink") {
  const line = new THREE.LineSegments(
    new THREE.EdgesGeometry(geometry),
    new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    })
  );
  line.userData.baseOpacity = opacity;
  line.userData.role = role;
  return line;
}

function fillMesh(geometry, color, opacity = 0.12, role = "panel") {
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
    })
  );
  mesh.userData.baseOpacity = opacity;
  mesh.userData.role = role;
  return mesh;
}

function setOpacity(object, amount) {
  const visible = amount > 0.02;
  object.visible = visible;
  object.traverse((child) => {
    if (!child.material) return;
    const base = child.userData.baseOpacity ?? 1;
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];
    materials.forEach((material) => {
      material.transparent = true;
      material.opacity = base * amount;
    });
  });
}

function disposeObject(object) {
  object.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      materials.forEach((material) => material.dispose());
    }
  });
}

function createDust(count, spread, color) {
  const positions = new Float32Array(count * 3);
  const seeds = new Float32Array(count);
  for (let i = 0; i < count; i += 1) {
    positions[i * 3] = (Math.random() - 0.5) * spread;
    positions[i * 3 + 1] = Math.random() * 3.8;
    positions[i * 3 + 2] = (Math.random() - 0.5) * spread;
    seeds[i] = Math.random() * Math.PI * 2;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color,
    size: 0.045,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  });
  material.userData = { role: "hiviz" };
  const points = new THREE.Points(geometry, material);
  points.userData.baseOpacity = 0.55;
  points.userData.seeds = seeds;
  return points;
}

function createHouse(palette) {
  const root = new THREE.Group();

  const survey = new THREE.Group();
  const grid = new THREE.GridHelper(14, 14, palette.blueprint, palette.rule);
  const gridMaterials = Array.isArray(grid.material) ? grid.material : [grid.material];
  gridMaterials.forEach((material, index) => {
    material.transparent = true;
    material.opacity = 0.45;
    material.userData = { role: index === 0 ? "blueprint" : "rule" };
  });
  grid.userData.baseOpacity = 0.45;
  survey.add(grid);

  const ring = fillMesh(
    new THREE.RingGeometry(4.8, 4.86, 64),
    palette.blueprint,
    0.5,
    "blueprint"
  );
  ring.material.side = THREE.DoubleSide;
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.01;
  survey.add(ring);

  [
    [-4.2, -3.1],
    [4.2, -3.1],
    [-4.2, 3.1],
    [4.2, 3.1],
  ].forEach(([x, z]) => {
    const pin = fillMesh(
      new THREE.ConeGeometry(0.12, 0.42, 4),
      palette.hiviz,
      0.95,
      "hiviz"
    );
    pin.position.set(x, 0.22, z);
    survey.add(pin);
  });
  root.add(survey);

  const site = new THREE.Group();
  const slabGeom = new THREE.BoxGeometry(4.6, 0.16, 3.5);
  site.add(fillMesh(slabGeom, palette.blueprint, 0.22, "blueprint"));
  site.add(edges(slabGeom, palette.blueprint, 0.85, "blueprint"));
  site.position.y = 0.08;
  root.add(site);

  const plan = new THREE.Group();
  const massGeom = new THREE.BoxGeometry(4, 3.2, 3);
  const mass = edges(massGeom, palette.ink, 0.55, "ink");
  mass.position.y = 1.76;
  plan.add(mass);
  const floorLine = edges(new THREE.BoxGeometry(4.02, 0.02, 3.02), palette.blueprint, 0.7, "blueprint");
  floorLine.position.y = 1.76;
  plan.add(floorLine);
  root.add(plan);

  const build = new THREE.Group();
  const storeyGeom = new THREE.BoxGeometry(4, 1.55, 3);
  const lower = fillMesh(storeyGeom, palette.panel, 0.16, "panel");
  lower.position.y = 0.95;
  const lowerEdge = edges(storeyGeom, palette.ink, 0.95, "ink");
  lowerEdge.position.y = 0.95;
  const upper = fillMesh(storeyGeom, palette.panel, 0.16, "panel");
  upper.position.y = 2.5;
  const upperEdge = edges(storeyGeom, palette.ink, 0.95, "ink");
  upperEdge.position.y = 2.5;
  const deck = fillMesh(new THREE.BoxGeometry(3.92, 0.05, 2.92), palette.hiviz, 0.28, "hiviz");
  deck.position.y = 1.76;
  build.add(lower, lowerEdge, upper, upperEdge, deck);

  const windowGeom = new THREE.BoxGeometry(0.72, 0.62, 0.06);
  const windowSlots = [
    [-1.15, 2.55],
    [0.05, 2.55],
    [1.15, 2.55],
    [-1.15, 1.05],
    [1.15, 1.05],
  ];
  windowSlots.forEach(([x, y]) => {
    const pane = fillMesh(windowGeom, palette.blueprint, 0.2, "blueprint");
    const frame = edges(windowGeom, palette.ink, 0.9, "ink");
    pane.position.set(x, y, 1.54);
    frame.position.copy(pane.position);
    build.add(pane, frame);
  });
  root.add(build);

  const finish = new THREE.Group();
  const roofShape = new THREE.Shape();
  roofShape.moveTo(-2.25, 0);
  roofShape.lineTo(0, 1.15);
  roofShape.lineTo(2.25, 0);
  roofShape.closePath();
  const roofGeom = new THREE.ExtrudeGeometry(roofShape, {
    depth: 3.2,
    bevelEnabled: false,
  });
  roofGeom.translate(0, 0, -1.6);
  const roofFill = fillMesh(roofGeom, palette.panel, 0.2, "panel");
  const roofEdge = edges(roofGeom, palette.ink, 0.95, "ink");
  const roof = new THREE.Group();
  roof.add(roofFill, roofEdge);
  roof.position.y = 3.28;
  finish.add(roof);

  const doorGeom = new THREE.BoxGeometry(0.72, 1.15, 0.08);
  const door = fillMesh(doorGeom, palette.ink, 0.35, "ink");
  const doorEdge = edges(doorGeom, palette.ink, 1, "ink");
  door.position.set(0, 0.78, 1.54);
  doorEdge.position.copy(door.position);
  const lintelGeom = new THREE.BoxGeometry(0.78, 0.08, 0.1);
  const lintel = fillMesh(lintelGeom, palette.hiviz, 1, "hiviz");
  lintel.position.set(0, 1.4, 1.58);
  finish.add(door, doorEdge, lintel);
  root.add(finish);

  const check = new THREE.Group();
  const scan = fillMesh(
    new THREE.BoxGeometry(4.4, 0.045, 3.3),
    palette.hiviz,
    0.75,
    "hiviz"
  );
  scan.position.y = 0.4;
  check.add(scan);
  root.add(check);

  const dust = createDust(90, 8.5, palette.hiviz);
  dust.userData.role = "hiviz";
  root.add(dust);

  const layers = { survey, site, plan, build, finish, check, dust, scan, lintel };

  function applyPalette(next) {
    root.traverse((child) => {
      const materials = child.material
        ? Array.isArray(child.material)
          ? child.material
          : [child.material]
        : [];
      materials.forEach((material) => {
        const role = material.userData?.role || child.userData.role;
        if (role && next[role]) material.color.set(next[role]);
      });
    });
  }

  function setBuild(progress) {
    const p = Math.min(1, Math.max(0, progress));
    const stage = p * 6;
    const fade = (from, to) => Math.min(1, Math.max(0, (stage - from) / (to - from)));

    setOpacity(survey, fade(0, 0.7) * (1 - fade(5.4, 6) * 0.25));
    setOpacity(site, fade(0.7, 1.5));
    setOpacity(plan, fade(1.6, 2.5) * (1 - fade(3.1, 3.8) * 0.7));
    setOpacity(build, fade(2.8, 4.1));
    setOpacity(finish, fade(3.8, 5));
    setOpacity(check, fade(4.2, 5.1) * (1 - fade(5.4, 6)));
    setOpacity(dust, fade(2.9, 4.2) * (1 - fade(5.2, 6) * 0.8));

    build.scale.y = 0.12 + fade(2.8, 4.2) * 0.88;
    build.position.y = (1 - build.scale.y) * 0.4;
    finish.position.y = (1 - fade(3.8, 5)) * 0.6;
    scan.position.y = 0.35 + fade(4.2, 5.6) * 3.2;

    return Math.min(5, Math.max(0, Math.floor(stage)));
  }

  return { root, layers, applyPalette, setBuild, dispose: () => disposeObject(root) };
}

function paletteFrom(el, darkProcess) {
  if (darkProcess) {
    return {
      ink: "#eceae4",
      panel: "#1e2225",
      blueprint: "#7fb2cc",
      rule: "#33393d",
      hiviz: "#f3b517",
    };
  }
  return {
    ink: readColor(el, "--ink", "#1b1e21"),
    panel: readColor(el, "--panel", "#f6f5f1"),
    blueprint: readColor(el, "--blueprint", "#2c5266"),
    rule: readColor(el, "--rule", "#c6c3bb"),
    hiviz: readColor(el, "--hiviz", "#f3b517"),
  };
}

export function useHeroBuilding(theme) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion()) return undefined;

    let renderer;
    try {
      renderer = makeRenderer(canvas);
    } catch {
      return undefined;
    }

    const rootEl = canvas.closest(".tc-root") || document.documentElement;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 80);
    camera.position.set(7.2, 4.6, 8.4);

    const house = createHouse(paletteFrom(rootEl, false));
    scene.add(house.root);
    canvas.parentElement?.classList.add("has-webgl");

    let mouseX = 0;
    let mouseY = 0;
    const onMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = (event.clientX - rect.left) / rect.width - 0.5;
      mouseY = (event.clientY - rect.top) / rect.height - 0.5;
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    const resize = () => sizeToParent(canvas, camera, renderer);
    resize();
    window.addEventListener("resize", resize);

    const started = performance.now();
    let frameId = 0;
    const tick = (now) => {
      frameId = requestAnimationFrame(tick);
      house.applyPalette(paletteFrom(rootEl, false));
      const intro = Math.min(1, (now - started) / 2400);
      const eased = 1 - Math.pow(1 - intro, 3);
      house.setBuild(0.18 + eased * 0.8);
      setOpacity(house.layers.check, 0);
      house.root.rotation.y = Math.sin(now * 0.00028) * 0.18 + mouseX * 0.5;
      camera.position.x = 7.2 + mouseX * 1.4;
      camera.position.y = 4.6 + mouseY * -0.6;
      camera.lookAt(0, 1.7, 0);
      const positions = house.layers.dust.geometry.attributes.position;
      const seeds = house.layers.dust.userData.seeds;
      for (let i = 0; i < seeds.length; i += 1) {
        const y = 0.3 + ((now * 0.00025 + seeds[i]) % 1) * 4.2;
        positions.setY(i, y);
      }
      positions.needsUpdate = true;
      renderer.render(scene, camera);
    };
    tick(started);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
      canvas.parentElement?.classList.remove("has-webgl");
      house.dispose();
      renderer.dispose();
    };
  }, [theme]);

  return canvasRef;
}

export function useProcessBuilding(theme) {
  const canvasRef = useRef(null);
  const captionRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section || prefersReducedMotion()) return undefined;

    let renderer;
    try {
      renderer = makeRenderer(canvas);
    } catch {
      return undefined;
    }

    const rootEl = canvas.closest(".tc-root") || document.documentElement;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 80);
    camera.position.set(8.1, 5.2, 9.2);

    const house = createHouse(paletteFrom(rootEl, true));
    scene.add(house.root);
    canvas.parentElement?.classList.add("has-webgl");

    const steps = section.querySelectorAll(".steps li");
    const resize = () => sizeToParent(canvas, camera, renderer);
    resize();
    window.addEventListener("resize", resize);

    let frameId = 0;
    const tick = (now) => {
      frameId = requestAnimationFrame(tick);
      house.applyPalette(paletteFrom(rootEl, true));

      const rect = section.getBoundingClientRect();
      const view = window.innerHeight || 1;
      const start = view * 0.72;
      const end = view * 0.18 - rect.height;
      const raw = (start - rect.top) / (start - end);
      const progress = Math.min(1, Math.max(0, raw));
      const stageIndex = house.setBuild(progress);

      if (captionRef.current) {
        captionRef.current.textContent = STAGE_LABELS[stageIndex];
      }
      steps.forEach((step, index) => {
        step.classList.toggle("is-active", index === stageIndex);
      });

      house.root.rotation.y = -0.35 + progress * 0.7;
      camera.position.x = 8.1 + Math.sin(now * 0.0002) * 0.25;
      camera.lookAt(0, 1.8, 0);

      const positions = house.layers.dust.geometry.attributes.position;
      const seeds = house.layers.dust.userData.seeds;
      for (let i = 0; i < seeds.length; i += 1) {
        positions.setY(i, ((now * 0.0003 + seeds[i]) % 1) * 4.4);
      }
      positions.needsUpdate = true;
      renderer.render(scene, camera);
    };
    tick(performance.now());

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      canvas.parentElement?.classList.remove("has-webgl");
      steps.forEach((step) => step.classList.remove("is-active"));
      house.dispose();
      renderer.dispose();
    };
  }, [theme]);

  return { canvasRef, captionRef, sectionRef };
}
